import { useMemo } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { formatCurrency } from '@/lib/utils';
import { calculateWealthProjection } from '@/lib/wealthProjection';
import { useDashboardStore } from '@/stores/dashboardStore';
import type { Property } from '@/types';

interface WealthChartProps {
  properties: Property[];
}

export function WealthChart({ properties }: WealthChartProps) {
  const { chartSettings } = useDashboardStore();

  const data = useMemo(() => {
    return calculateWealthProjection(properties, chartSettings);
  }, [properties, chartSettings]);

  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-muted-foreground">
        Keine Immobilien im Bestand für die Projektion.
      </div>
    );
  }

  // Daten für Chart transformieren (Cent zu Euro)
  const chartData = data.map(d => ({
    year: `Jahr ${d.year}`,
    'Eigenkapital': d.equity / 100,
    'Cashflow (kumuliert)': d.cumulativeCashflow / 100,
  }));

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-card border border-border rounded-lg p-3 shadow-lg">
          <p className="font-medium mb-2">{label}</p>
          {payload.map((entry: any, index: number) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: {formatCurrency(entry.value * 100)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <ResponsiveContainer width="100%" height={300}>
      <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id="colorEquity" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3} />
            <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
          </linearGradient>
          <linearGradient id="colorCashflow" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
            <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
        <XAxis
          dataKey="year"
          tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
          axisLine={{ stroke: 'hsl(var(--border))' }}
        />
        <YAxis
          tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
          axisLine={{ stroke: 'hsl(var(--border))' }}
          tickFormatter={(value) => `${(value / 1000).toFixed(0)}k`}
        />
        <Tooltip content={<CustomTooltip />} />
        <Legend />
        <Area
          type="monotone"
          dataKey="Eigenkapital"
          stroke="#22c55e"
          fillOpacity={1}
          fill="url(#colorEquity)"
          strokeWidth={2}
        />
        <Area
          type="monotone"
          dataKey="Cashflow (kumuliert)"
          stroke="#3b82f6"
          fillOpacity={1}
          fill="url(#colorCashflow)"
          strokeWidth={2}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}
